
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
import logger from '@/lib/logging';

/**
 * Safely convert any date-like value (string, number, Date, Firestore Timestamp) to a Date object.
 */
export function safeToDate(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  
  try {
    // Check for Firestore Timestamp
    if (typeof value.toDate === 'function') {
      const d = value.toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    }
    
    // Check for other Timestamp-like objects { seconds/(_seconds), nanoseconds/(_nanoseconds) }
    const seconds = value.seconds ?? value._seconds;
    const nanoseconds = value.nanoseconds ?? value._nanoseconds;
    
    if (typeof seconds === 'number') {
      const d = new Date(seconds * 1000 + (nanoseconds || 0) / 1000000);
      return isNaN(d.getTime()) ? null : d;
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  } catch (e) {
    return null;
  }
}

/**
 * Format a date for display. Handles Firestore Timestamps, ISO strings, Dates, and
 * JSON-serialized Firestore timestamps ({ seconds, nanoseconds } from REST/API responses).
 */
export function formatDateSafe(dateValue: any, formatString: string = "MM/dd/yyyy"): string {
  const date = safeToDate(dateValue);
  if (!date) return 'N/A';
  try {
    return format(date, formatString);
  } catch (error) {
    logger.error("Date formatting error:", error);
    return 'N/A';
  }
}

export function formatDateLocale(dateValue: any, options?: Intl.DateTimeFormatOptions): string {
  const date = safeToDate(dateValue);
  if (!date) return 'N/A';
  try {
    return date.toLocaleString(typeof navigator !== 'undefined' ? navigator.language : 'en-US', options);
  } catch (error) {
    logger.error("Date formatting error:", error);
    return 'N/A';
  }
}

/**
 * Format a calendar date (no time) using the user's locale.
 * Uses `navigator.language` when `locales` is omitted (browser/OS language).
 * Pass `locales` to override (e.g. for tests).
 */
export function formatDateOnlyLocale(
  dateValue: any,
  locales?: Intl.LocalesArgument
): string {
  const date = safeToDate(dateValue);
  if (!date) return 'N/A';
  try {
    const loc =
      locales ??
      (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
    return new Intl.DateTimeFormat(loc, { dateStyle: 'short' }).format(date);
  } catch (error) {
    logger.error('Date formatting error:', error);
    return 'N/A';
  }
}

/** Milliseconds for sorting records; prefers explicit `date`, then `createdAt`. */
export function getRecordSortMillis(record: {
  date?: unknown;
  createdAt?: unknown;
}): number {
  const dateMs = safeToDate(record.date)?.getTime() ?? 0;
  const createdMs = safeToDate(record.createdAt)?.getTime() ?? 0;
  return dateMs || createdMs;
}

/** Newest-first by `date` (fallback `createdAt`). */
export function sortRecordsBySortDateDesc<T extends { date?: unknown; createdAt?: unknown }>(
  records: T[]
): T[] {
  return [...records].sort((a, b) => getRecordSortMillis(b) - getRecordSortMillis(a));
}

/** Latest record by `date` (fallback `createdAt`), or null when empty. */
export function pickLatestRecordBySortDate<T extends { date?: unknown; createdAt?: unknown }>(
  records: T[]
): T | null {
  if (records.length === 0) return null;
  return sortRecordsBySortDateDesc(records)[0] ?? null;
}

/**
 * Format a date with time (hours+minutes) using the user's locale.
 * Uses `navigator.language` when `locales` is omitted (browser/OS language).
 * Pass `locales` to override (e.g. for tests).
 *
 * Single source of truth for compact "date + short time" labels (e.g. the
 * Previous Assessments dropdowns in Posture Analysis and SOP Assessment).
 */
export function formatDateTimeLocale(
  dateValue: any,
  locales?: Intl.LocalesArgument
): string {
  const date = safeToDate(dateValue);
  if (!date) return 'N/A';
  try {
    const loc =
      locales ??
      (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
    return new Intl.DateTimeFormat(loc, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch (error) {
    logger.error('Date formatting error:', error);
    return 'N/A';
  }
}

/**
 * Formats a phone number to international format without spaces
 * Converts phone numbers to format: +13108958981 (no spaces)
 * @param phone - The phone number string (may contain spaces, dashes, parentheses, etc.)
 * @returns Formatted phone number in international format without spaces, or empty string if invalid
 */
export function formatPhoneInternational(phone: string | undefined | null): string {
  if (!phone) return '';

  // Remove all non-digit characters except +
  let cleaned = phone.replace(/[^\d+]/g, '');

  // If it doesn't start with +, add it (assuming US/Canada if no country code)
  if (!cleaned.startsWith('+')) {
    // If it starts with 1 and has 11 digits, it's already a US number with country code
    if (cleaned.startsWith('1') && cleaned.length === 11) {
      cleaned = '+' + cleaned;
    } else if (cleaned.length === 10) {
      // US/Canada number without country code, add +1
      cleaned = '+1' + cleaned;
    } else {
      // Add + prefix if missing
      cleaned = '+' + cleaned;
    }
  }

  return cleaned;
}

/**
 * Detect browser name and version from user agent
 */
export function detectBrowser(): { name: string; version: string } {
  if (typeof navigator === 'undefined') return { name: 'Unknown', version: 'Unknown' };

  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let version = 'Unknown';

  // Check for Edge (Legacy and Chromium based)
  // Note: Chromium Edge usually includes 'Edg/'
  if (ua.includes('Edg/')) {
    browser = 'Edge';
    version = ua.match(/Edg\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Edge/')) { // Legacy Edge
    browser = 'Edge (Legacy)';
    version = ua.match(/Edge\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Firefox/')) {
    browser = 'Firefox';
    version = ua.match(/Firefox\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
    browser = 'Chrome';
    version = ua.match(/Chrome\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari';
    version = ua.match(/Version\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Opera/') || ua.includes('OPR/')) {
    browser = 'Opera';
    version = ua.match(/(?:Opera|OPR)\/([0-9.]+)/)?.[1] || 'Unknown';
  }

  return { name: browser, version };
}
